import { useState } from "react";
import { Check, Copy } from "lucide-react";

/*
 * A linha digitável como recurso tipográfico — motivo de assinatura 1 do doc
 * 06a §3.6.
 *
 * "Os 47 dígitos em cinco grupos, IBM Plex Mono, separadores em peso mais leve e
 * `--texto-3`, com botão de copiar embutido."
 *
 * O agrupamento é o da ficha impressa, e não decoração: é assim que o número
 * aparece no boleto que o sacado tem na mão, e ler em voz alta ao telefone com
 * outro agrupamento é como o suporte perde cinco minutos.
 */
const GRUPOS = [5, 5, 5, 6, 5, 6, 1, 14] as const;

/** Quebra os 47 dígitos nos grupos da ficha de compensação. */
export function agrupar(digitos: string): string[] {
  const limpo = digitos.replace(/\D/g, "");
  const partes: string[] = [];
  let posicao = 0;

  for (const tamanho of GRUPOS) {
    if (posicao >= limpo.length) break;
    partes.push(limpo.slice(posicao, posicao + tamanho));
    posicao += tamanho;
  }

  // Sobra vira um grupo próprio em vez de sumir: uma linha com mais dígitos que
  // o esperado é um defeito para investigar, e escondê-la esconde o defeito.
  if (posicao < limpo.length) partes.push(limpo.slice(posicao));

  return partes;
}

export function LinhaDigitavel({ valor }: { valor: string }) {
  const [copiado, setCopiado] = useState(false);
  const grupos = agrupar(valor);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(valor.replace(/\D/g, ""));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      // `navigator.clipboard` exige contexto seguro e permissão. Falhar em
      // silêncio é certo aqui: o número continua na tela, selecionável, e um
      // alerta sobre a área de transferência não ajuda quem só quer o número.
    }
  }

  return (
    <div className="flex items-center gap-1 rounded-controle border border-fio bg-superficie-2 px-3 py-2">
      <span className="dados min-w-0 text-[13px] leading-6 tracking-tight">
        {grupos.map((grupo, indice) => (
          <span key={`${indice}-${grupo}`}>
            {indice > 0 && <span className="px-0.5 font-normal text-texto-3">.</span>}
            {grupo}
          </span>
        ))}
      </span>

      <button
        type="button"
        onClick={() => void copiar()}
        // O rótulo acessível não muda quando o estado muda: um botão que se
        // renomeia confunde quem navega por teclado e volta nele depois.
        aria-label="Copiar a linha digitável"
        className="ml-auto rounded-controle p-1 text-texto-2 hover:bg-fio hover:text-texto"
      >
        {copiado ? (
          <Check aria-hidden="true" className="size-4 text-pago" />
        ) : (
          <Copy aria-hidden="true" className="size-4" />
        )}
        {/* O estado vai por texto, para leitor de tela, sem ocupar espaço. */}
        <span className="sr-only" role="status">
          {copiado ? "Copiada" : ""}
        </span>
      </button>
    </div>
  );
}
